import { Modal, Button, Row, Col, Card, ListGroup, Spinner, Tabs, Tab, Alert, Table, Form } from "react-bootstrap";
import { CheckCircle, XCircle } from "react-bootstrap-icons";
import { formatCurrency } from "../../../store/config";

function BeneficiaryRequestDetailsModal({
    show,
    onHide,
    request,
    processingAction,
    transactionReference,
    setTransactionReference,
    handleUpdateStatus,
    StatusBadge
}) {
    if (!request) return null;

    const canApproveOrReject = request.status === "pending";
    const canProcess = request.status === "approved";

    const renderDocumentLinks = () => {
        const documentLinks = [];

        for (const [key, value] of Object.entries(request.documents || {})) {
            if (value) {
                const formattedKey = key
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, str => str.toUpperCase());

                documentLinks.push(
                    <ListGroup.Item key={key} className="d-flex justify-content-between align-items-center">
                        <span>{formattedKey}</span>
                        <Button
                            variant="outline-success"
                            size="sm"
                            href={value}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            View Document
                        </Button>
                    </ListGroup.Item>
                );
            }
        }

        return documentLinks.length > 0 ? documentLinks : (
            <ListGroup.Item>No documents available</ListGroup.Item>
        );
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" backdrop="static" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    Beneficiary Withdrawal Request
                    <div className="ms-2 d-inline-block">
                        <StatusBadge status={request.status} />
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                <div className="mb-4">
                    <Row>
                        <Col md={6}>
                            <small className="text-muted d-block">Request Date</small>
                            <div>{new Date(request.createdAt || request.requestDate).toLocaleDateString()}</div>
                        </Col>
                        <Col md={6}>
                            <small className="text-muted d-block">Status</small>
                            <StatusBadge status={request.status} />
                        </Col>
                    </Row>
                </div>

                {request.status === "rejected" && request.rejectionReason && (
                    <Alert variant="danger" className="mb-4">
                        <strong>Rejection Reason:</strong> {request.rejectionReason}
                    </Alert>
                )}

                {request.status === "processed" && request.transactionReference && (
                    <Alert variant="success" className="mb-4">
                        <strong>Transaction Reference:</strong> {request.transactionReference}
                        {request.processedAt && (
                            <>
                                <br />
                                <strong>Processed On:</strong> {new Date(request.processedAt).toLocaleString()}
                            </>
                        )}
                    </Alert>
                )}

                <Tabs defaultActiveKey="details" className="mb-4">
                    <Tab eventKey="details" title="Request Details">
                        <Row className="mt-4">
                            <Col md={6} className="mb-4">
                                <Card className="h-100">
                                    <Card.Header className="bg-light">Deceased Member Details</Card.Header>
                                    <ListGroup variant="flush">
                                        <ListGroup.Item>
                                            <strong>Member ID:</strong> {request.deceasedMemberId}
                                        </ListGroup.Item>
                                        <ListGroup.Item>
                                            <strong>Date of Death:</strong> {new Date(request.dateOfDeath).toLocaleDateString()}
                                        </ListGroup.Item>
                                    </ListGroup>
                                </Card>
                            </Col>
                            <Col md={6} className="mb-4">
                                <Card className="h-100">
                                    <Card.Header className="bg-light">Beneficiary Details</Card.Header>
                                    <ListGroup variant="flush">
                                        <ListGroup.Item>
                                            <strong>Name:</strong> {request.beneficiary.name}
                                        </ListGroup.Item>
                                        <ListGroup.Item>
                                            <strong>Email:</strong> {request.beneficiary.email}
                                        </ListGroup.Item>
                                        <ListGroup.Item>
                                            <strong>Phone:</strong> {request.beneficiary.phone}
                                        </ListGroup.Item>
                                        <ListGroup.Item>
                                            <strong>Relationship:</strong> {request.beneficiary.relationship}
                                        </ListGroup.Item>
                                    </ListGroup>
                                </Card>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6} className="mb-4">
                                <Card className="h-100">
                                    <Card.Header className="bg-light">Bank Details</Card.Header>
                                    <ListGroup variant="flush">
                                        <ListGroup.Item>
                                            <strong>Bank Name:</strong> {request.bankDetails.bankName}
                                        </ListGroup.Item>
                                        <ListGroup.Item>
                                            <strong>Account Name:</strong> {request.bankDetails.accountName}
                                        </ListGroup.Item>
                                        <ListGroup.Item>
                                            <strong>Account Number:</strong> {request.bankDetails.accountNumber}
                                        </ListGroup.Item>
                                    </ListGroup>
                                </Card>
                            </Col>
                            <Col md={6} className="mb-4">
                                <Card className="h-100 border-success">
                                    <Card.Header className="bg-success text-white">Financial Summary</Card.Header>
                                    <Table borderless className="mb-0">
                                        <tbody>
                                            <tr>
                                                <th>Total Savings</th>
                                                <td className="text-end">
                                                    {formatCurrency(request.calculatedAmounts.totalSavings)}
                                                </td>
                                            </tr>
                                            <tr>
                                                <th>Total Loans</th>
                                                <td className="text-end">
                                                    ({formatCurrency(request.calculatedAmounts.totalLoans)})
                                                </td>
                                            </tr>
                                            <tr>
                                                <th>Net Amount</th>
                                                <td className="text-end">
                                                    {formatCurrency(request.calculatedAmounts.netAmount)}
                                                </td>
                                            </tr>
                                            <tr className="table-success">
                                                <th className="h5">Amount Due</th>
                                                <td className="text-end h5 fw-bold">
                                                    {formatCurrency(request.calculatedAmounts.amountDue || request.calculatedAmounts.netAmount)}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </Card>
                            </Col>
                        </Row>
                    </Tab>

                    <Tab eventKey="savings" title="Savings Details">
                        {request.calculatedAmounts.savingsItems && request.calculatedAmounts.savingsItems.length > 0 ? (
                            <Table bordered hover responsive className="mt-4">
                                <thead className="table-success">
                                    <tr>
                                        <th>Savings Type</th>
                                        <th className="text-end">Code</th>
                                        <th className="text-end">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {request.calculatedAmounts.savingsItems.map((item, index) => (
                                        <tr key={index}>
                                            <td>{item.name}</td>
                                            <td className="text-end">{item.code}</td>
                                            <td className="text-end">{formatCurrency(item.value)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="table-light">
                                    <tr>
                                        <th colSpan="2">Total Savings</th>
                                        <th className="text-end">
                                            {formatCurrency(request.calculatedAmounts.totalSavings)}
                                        </th>
                                    </tr>
                                </tfoot>
                            </Table>
                        ) : (
                            <Alert variant="info" className="mt-4">No savings items found</Alert>
                        )}
                    </Tab>

                    <Tab eventKey="loans" title="Loan Details">
                        {request.calculatedAmounts.loanItems && request.calculatedAmounts.loanItems.length > 0 ? (
                            <Table bordered hover responsive className="mt-4">
                                <thead className="table-danger">
                                    <tr>
                                        <th>Loan Type</th>
                                        <th className="text-end">Code</th>
                                        <th className="text-end">Outstanding Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {request.calculatedAmounts.loanItems.map((item, index) => (
                                        <tr key={index}>
                                            <td>{item.name}</td>
                                            <td className="text-end">{item.code}</td>
                                            <td className="text-end">{formatCurrency(item.value)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="table-light">
                                    <tr>
                                        <th colSpan="2">Total Loans</th>
                                        <th className="text-end">
                                            {formatCurrency(request.calculatedAmounts.totalLoans)}
                                        </th>
                                    </tr>
                                </tfoot>
                            </Table>
                        ) : (
                            <Alert variant="info" className="mt-4">No loan items found</Alert>
                        )}
                    </Tab>

                    <Tab eventKey="documents" title="Documents">
                        <Card className="mt-4">
                            <Card.Header className="bg-light">Supporting Documents</Card.Header>
                            <ListGroup variant="flush">
                                {renderDocumentLinks()}
                            </ListGroup>
                        </Card>
                    </Tab>
                </Tabs>

                {canProcess && (
                    <div className="mt-4">
                        <Form.Group className="mb-3">
                            <Form.Label>Transaction Reference</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter transaction reference or payment ID"
                                value={transactionReference}
                                onChange={(e) => setTransactionReference(e.target.value)}
                            />
                            <Form.Text className="text-muted">
                                Enter the bank transaction reference or payment ID after processing the payment
                            </Form.Text>
                        </Form.Group>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Close
                </Button>

                {canApproveOrReject && (
                    <>
                        <Button
                            variant="danger"
                            onClick={() => handleUpdateStatus("rejected")}
                            disabled={processingAction}
                        >
                            {processingAction ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" /> Rejecting...
                                </>
                            ) : (
                                <>
                                    <XCircle className="me-1" /> Reject Request
                                </>
                            )}
                        </Button>
                        <Button
                            variant="success"
                            onClick={() => handleUpdateStatus("approved")}
                            disabled={processingAction}
                        >
                            {processingAction ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" /> Approving...
                                </>
                            ) : (
                                <>
                                    <CheckCircle className="me-1" /> Approve Request
                                </>
                            )}
                        </Button>
                    </>
                )}

                {canProcess && (
                    <Button
                        variant="success"
                        onClick={() => handleUpdateStatus("processed")}
                        disabled={processingAction || !transactionReference?.trim()}
                    >
                        {processingAction ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" /> Processing...
                            </>
                        ) : (
                            <>Mark as Processed</>
                        )}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
}

export default BeneficiaryRequestDetailsModal;